import { useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { ArrowLeft, Eye, EyeOff, PanelRight, Pencil, Pin, PinOff, SquarePen, Trash2 } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { conversationDisplayTitle } from '../../../lib/conversation-display-title';
import type { Conversation } from '../../../lib/conversations';
import { useConversationActions, CONVERSATIONS_PAGE } from '../../../lib/use-conversation-actions';
import { setRecapVisibleFor } from '../../../lib/recap-preference';
import { useRecapVisible } from '../../../lib/use-recap-visible';
import { DeleteConversationConfirm } from '../conversations/DeleteConversationConfirm';
import { RenameConversationDialog } from '../conversations/RenameConversationDialog';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { AgentsIslandBadge } from '../AgentsIslandBadge';
import { Button, IconButton } from '../ui';
import { OverflowMenu, type OverflowMenuAction } from '../ui/OverflowMenu';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useConversationMonitorLongPress } from './use-conversation-monitor';
import { headerStyle, newChatTitleStyle } from './styles';
import styles from './ChatHeader.module.css';
import { CloseIcon, ExpandIcon, FilesIcon, ShrinkIcon } from './icons';

export interface ChatHeaderProps {
  chatsScreen?: boolean;
  onBackToChats?: () => void;
  narrow: boolean;
  fullscreen: boolean;
  isNewChat: boolean;
  headerTitle: string | undefined;
  /** A badge drawn after the title — the "Meeting" pill over a meeting's write-up. Wide only, like the title. */
  badge?: ReactNode;
  /** A quiet second line under the title — the meeting's own time and duration. Wide only, like the title. */
  subtitle?: string;
  onNewConversation: () => void;
  onCompose: () => void;
  onExpand: () => void;
  onShrink: () => void;
  onMinimize: () => void;
  // Opens the per-chat Files/Media panel. Undefined => no listable conversation
  // yet (a brand-new, never-sent chat), so the button is hidden.
  onOpenFiles?: () => void;
  /** The conversation this header is for. Undefined on a brand-new chat, which has no row to act on yet —
   *  so the pin/rename/delete items are simply absent rather than present and inert. */
  conversation?: Conversation;
  /** The chat status panel's toggle (`ChatStatusButton`), under `symphonyChatStatusPanelWeb` only. It draws
   *  nothing until the conversation has created something. Given the class the mobile pill puts on its own
   *  icons, so it sits in the pill like them. */
  renderStatusButton?: (className: string | undefined) => ReactNode;
  /** The same toggle from the ⋮ menu, beside Files. Undefined until the conversation has created something. */
  onToggleStatus?: () => void;
  statusOpen?: boolean;
  /** Phone width: the island opens the chat status itself, dropped from under this header (`panel` is handed
   *  the header as its anchor). Replaces the button and the ⋮ item there; undefined until the conversation has
   *  created something. */
  islandStatus?: {
    conversationId: string | null;
    open: boolean;
    /** The panel dropped open by itself because the chat just made something; it closes again on its own. */
    peek?: boolean;
    onToggle: () => void;
    panel: (anchor: HTMLElement | null) => ReactNode;
  };
}

// Mobile: the ⋮ overflow rides a 36px white circle, matching the BrandHeader hamburger
// (Figma "Header" 14701:127253 — Mobile Web Default). The 30px ghost trigger centres inside it.
// Under the title, in the secondary ink — the meeting's own span, the same type the agent page's
// history row sets its time in.
const headerSubtitleStyle: CSSProperties = {
  padding: '0 6px',
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 'var(--sym-weight-medium)',
  letterSpacing: '-0.02em',
  color: 'var(--ds-text-secondary)',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export function ChatHeader({
  chatsScreen,
  onBackToChats,
  narrow,
  fullscreen,
  isNewChat,
  headerTitle,
  badge,
  subtitle,
  onNewConversation,
  onCompose,
  onExpand,
  onShrink,
  onMinimize,
  onOpenFiles,
  conversation,
  renderStatusButton,
  onToggleStatus,
  statusOpen,
  islandStatus,
}: ChatHeaderProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const flow = useConversationActions(CONVERSATIONS_PAGE);
  const recapVisible = useRecapVisible(conversation?.id);
  // The overflow menu hangs from the header's bottom edge (Figma), not the trigger's — so it clears the
  // header by the same amount whether the trigger is the 30px desktop button or the 36px mobile circle.
  const headerRef = useRef<HTMLElement>(null);
  // Dev-only, VPN/dev-build gated: HOLD expand/shrink to open this thread in Symphony Live. Inert —
  // and request-free — for a real user, and inert on a brand-new chat with no conversation to monitor.
  const monitor = useConversationMonitorLongPress(conversation?.id);

  const newChat = () => {
    logPageClickBi({ page_name: 'chat', button_name: 'new_chat' });
    (narrow && !chatsScreen ? onCompose : onNewConversation)();
  };
  // Whether the standalone New-Chat affordance shows (the compose icon on mobile and in the dock, the labeled
  // button when expanded — the /chats split column then drops it, see below): not already sitting on an
  // empty new chat (unless this is the /chats list on mobile).
  const newChatShown = !isNewChat || (chatsScreen && narrow);

  // New Chat is a labeled primary action beside the overflow on both widths.
  // The overflow keeps secondary actions, including manual rename of the open chat.
  const menuActions: OverflowMenuAction[] = [];
  if (onOpenFiles) {
    menuActions.push({
      key: 'chat-files',
      label: t('chatFiles.title'),
      icon: <FilesIcon />,
      onSelect: () => {
        logPageClickBi({ page_name: 'chat', button_name: 'open_chat_files' });
        onOpenFiles();
      },
    });
  }
  if (onToggleStatus) {
    menuActions.push({
      key: 'chat-status',
      label: statusOpen ? t('chat.status.hide') : t('chat.status.title'),
      icon: <PanelRight size={16} aria-hidden />,
      onSelect: () => {
        logPageClickBi({ page_name: 'chat', button_name: statusOpen ? 'hide_chat_status_menu' : 'show_chat_status_menu' });
        onToggleStatus();
      },
    });
  }
  // The same three actions the conversation's row in the sidebar offers, for the conversation you are
  // actually IN — reached here because that is where a user thinks about the chat they are reading, and
  // because the mobile header is the only chrome on that screen. The labels, order and destructive skin
  // match the row menu exactly; the writes are the same shared flow, so the two cannot diverge.
  // Guard on the ID, not the conversation: a row with no server id cannot be pinned, renamed or deleted —
  // every one of those writes addresses it by id — and guarding on the id is also what narrows it to a string.
  const convId = conversation?.id;
  if (convId) {
    // The SAME name→start-time→"Untitled" rule the chats list and the rail apply, so the delete
    // confirm and the rename draft call a thread what the row that opened them calls it. Without this
    // an unnamed thread read its start time in the list and "Untitled" in its own sheet — the very
    // split this fix exists to close, one surface further in. iOS does the same in `rowActionsTitle`.
    // `t` is typed as possibly-undefined and the dialogs' target takes a real string, hence the `|| ''`.
    const title: string = conversationDisplayTitle(
      conversation?.name, conversation?.createdDate, t('common.untitled') || '',
    );
    const pinned = Boolean(conversation?.pinned);
    menuActions.push(
      {
        key: 'pin',
        label: pinned ? t('conversations.unpin') : t('conversations.pin'),
        icon: pinned ? <PinOff size={16} aria-hidden /> : <Pin size={16} aria-hidden />,
        onSelect: () => void flow.togglePin({ id: convId, pinned }),
      },
      {
        key: 'rename',
        label: t('conversations.rename.action'),
        icon: <Pencil size={16} aria-hidden />,
        onSelect: () => flow.requestRename({ id: convId, title }),
      },
      // The way back from the recap banner's ×, which hides the line on THIS conversation. It names the
      // outcome the way the pin item does, so the label is what pressing it will do.
      {
        key: 'recap',
        label: recapVisible ? t('chat.recap.hide') : t('chat.recap.show'),
        icon: recapVisible ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />,
        onSelect: () => {
          logPageClickBi({ page_name: 'chat', button_name: recapVisible ? 'disable_recap' : 'enable_recap' });
          setRecapVisibleFor(convId, !recapVisible);
        },
      },
      {
        key: 'delete',
        label: t('conversations.delete.action'),
        icon: <Trash2 size={16} aria-hidden />,
        destructive: true,
        onSelect: () => flow.requestDelete({ id: convId, title }),
      },
    );
  }

  return (
    <>
    <header
      ref={headerRef}
      style={{
        ...headerStyle,
        // The centred island below is absolute; without this it would resolve against the fixed chat surface
        // and centre on the whole panel rather than on this row.
        position: 'relative',
        ...(narrow || isNewChat ? { borderBottom: 'none' } : null),
        ...(narrow ? { justifyContent: 'flex-end' } : null),
      }}
    >
      {narrow ? null : (
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={newChatTitleStyle}>{headerTitle}</span>
            {badge}
          </span>
          {subtitle && <span style={headerSubtitleStyle}>{subtitle}</span>}
        </span>
      )}

      {/* CENTRED, whenever the chat COVERS the screen — mirroring native's chat header, which centres the
          island and draws it before the overflow menu. That is the one case needing its own copy: the chat
          surface is a fixed z-50 layer, so AppFrame's absolute z-6 top-bar island is covered by it and cannot
          be seen at all (which is why those modes used to have none). On a phone every open chat covers the
          screen — `fullscreen` is a wide-only mode — so narrow counts too. Docked chat keeps using the top-bar
          island; rendering here as well would put TWO islands on screen and register the chip slot twice.
          The /chats screen follows the SAME rule: its wide SPLIT column is a dock (the list's title row carries
          the island beside it, so none here), while a chat that covers the list — narrow, or grown to
          `fullscreen` by ⤢ — needs its own island like any other. Narrow /chats draws its back arrow AFTER
          the island, beside the actions: the hamburger holds the top-left corner, so the back button there
          would sit under it, and it is icon-only so the island still fits a 390px phone. */}
      {(narrow || fullscreen) && (
        // Wide has room to centre on the whole row. A phone does not: the BrandHeader hamburger occupies the
        // top-left, so the island takes the leftover space as a flex item and centres inside THAT, clearing
        // the 44px hamburger with paddingInlineStart.
        <div
          style={
            narrow
              ? { flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', paddingInlineStart: 44 }
              : { position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex' }
          }
        >
          <GenericEventsRouteProvider value="agents-island">
            <AgentsIslandBadge
              side="center"
              {...(narrow && islandStatus
                ? {
                    statusOpen: islandStatus.open,
                    onOpenStatus: () => {
                      logPageClickBi({
                        page_name: 'chat',
                        button_name: islandStatus.open ? 'hide_chat_status' : 'show_chat_status',
                        ...(islandStatus.conversationId ? { conversation_id: islandStatus.conversationId } : {}),
                      });
                      islandStatus.onToggle();
                    },
                  }
                : {})}
            />
          </GenericEventsRouteProvider>
        </div>
      )}
      {chatsScreen && narrow && (
        <IconButton variant="text" onClick={onBackToChats} aria-label={t('chats.title')} title={t('chats.title')}>
          <ArrowLeft size={16} className="dir-flip" aria-hidden />
        </IconButton>
      )}
      {/* Keep New Chat visible without opening the secondary actions menu. It carries its LABEL only when
          the chat is EXPANDED — every other state is a narrow column that the label does not fit in:
          - narrow (phone / stacked): the labeled pill sat in the SAME flex row as the centered island's
            flex:1 container, so its text width ate into the island's available space and pushed the island
            visibly off-center (reported — the island "is not located well" beside a wide New Chat button);
          - side dock and the /chats split column (both `!narrow && !fullscreen`, since `liveFullscreen` is
            forced off on the Chats screen): the header is a ~dock-width row, so the pill shrank onto two
            lines and read "New / Chat" (reported, with a screenshot).
          So the signal is the chat's own width, NOT the viewport's — a wide viewport says nothing about how
          much room this header has. Icon-only keeps its accessible name, so nothing is lost but the glyph.
          Exception to three-surface parity, written down: the side dock and the expanded chat are WEB-ONLY
          layout modes; iOS and Android always render the phone header, which is this component's `narrow`
          pill (`ChatHeader.swift` / `chat/Chat.kt`), so the wide variants have nothing to mirror. */}
      <div className={styles.actions}>
        {narrow ? (
          // Mobile: ONE white pill holds the compose (new chat) icon and the ⋮ overflow
          // (Figma 20312-114388 / 20312-114401), replacing the old standalone Plus + separate ⋮ circle.
          // Rendered only when it would hold something.
          (newChatShown || menuActions.length > 0) && (
            <div className={styles.mobilePill}>
              {renderStatusButton?.(styles.pillIcon)}
              {newChatShown && (
                <IconButton size="small" className={styles.pillIcon} onClick={newChat} aria-label={t('chats.newChat')} title={t('chats.newChat')}>
                  <SquarePen size={16} aria-hidden />
                </IconButton>
              )}
              {menuActions.length > 0 && (
                <OverflowMenu actions={menuActions} ariaLabel={t('chat.moreActions')} triggerClassName={styles.pillTrigger} blockAnchorRef={headerRef} blockAnchorGap={8} />
              )}
            </div>
          )
        ) : (
          <>
            {renderStatusButton?.(undefined)}
            {newChatShown && (
              fullscreen ? (
                // The SAME outlined, label-only button as the /chats title row (Figma 20312-114342 /
                // 20312-113774), so the list and the expanded chat show one New Chat.
                <Button variant="outlined" className={styles.newChat} onClick={newChat} aria-label={t('chats.newChat')}>
                  {t('chats.newChat')}
                </Button>
              ) : chatsScreen ? null : (
                // Docked beside the /chats list the header carries no New Chat — the list's title row
                // already has one right next to it (Figma 20312-113864: "⋮ ⤢ ×"). Docked over any other page
                // it is the compose glyph, the same one the mobile pill uses (Figma 20312-114317).
                <IconButton size="small" onClick={newChat} aria-label={t('chats.newChat')} title={t('chats.newChat')}>
                  <SquarePen size={16} aria-hidden />
                </IconButton>
              )
            )}
            {menuActions.length > 0 && (
              // Wide: the ⋮ hovers like the expand/close buttons beside it and wears a selected fill while
              // its menu is open (Figma "Chat Header" 14692:214629).
              <OverflowMenu actions={menuActions} ariaLabel={t('chat.moreActions')} triggerClassName={styles.triggerSelectable} blockAnchorRef={headerRef} />
            )}
          </>
        )}
        {/* ⤢ grow/shrink and × close, on EVERY wide chat including the /chats split column. They used to be
            `!chatsScreen`, which is the bug QA filed twice: the /chats header offered "+ ⋮" where the design
            (Figma "Chat header" 18693:74801) draws "⋮ ⤢ ×", so a chat opened from the list could neither be
            grown to full width nor closed — the docked chat over any other page had both. The /chats column
            IS a dock, so it takes the dock's own chrome; only `narrow` (one pane at a time, with a "← Chats"
            back button doing the closing) still has neither. */}
        {!narrow &&
          (fullscreen ? (
            <IconButton size="small" {...monitor.handlers} onClick={() => { if (monitor.consumeLongPress()) return; logPageClickBi({ page_name: 'chat', button_name: 'shrink_chat' }); onShrink(); }} aria-label={t('chat.float')} title={t('chat.floatShort')}>
              <ShrinkIcon />
            </IconButton>
          ) : (
            <IconButton size="small" {...monitor.handlers} onClick={() => { if (monitor.consumeLongPress()) return; logPageClickBi({ page_name: 'chat', button_name: 'expand_chat' }); onExpand(); }} aria-label={t('chat.expand')} title={t('chat.expandShort')}>
              <ExpandIcon />
            </IconButton>
          ))}
        {!narrow && (
          <IconButton size="small" onClick={() => { logPageClickBi({ page_name: 'chat', button_name: 'minimize_chat' }); onMinimize(); }} aria-label={t('chat.minimize')} title={t('chat.minimizeShort')}>
            <CloseIcon />
          </IconButton>
        )}
      </div>
    </header>
    {narrow && (islandStatus?.open || islandStatus?.peek) && islandStatus.panel(headerRef.current)}
    <DeleteConversationConfirm flow={flow} />
    <RenameConversationDialog flow={flow} />
    </>
  );
}
